import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { subscribeStomp } from "../stompClient";
import { autoFeedActualsFrameToAction } from "./autoFeedActualsFrame";
import { AUTO_FEED_ACTUALS_STARTED } from "../types";

/**
 * App-level subscription to the auto-feed actuals cascade STOMP topic.
 *
 * Server-triggered: there's no FE call site that starts the cascade, so we
 * mount this once at App level. The subscription rides on the shared STOMP
 * singleton (no separate SockJS session).
 *
 * Multi-job support: each STARTED frame spawns a new job UUID (generated
 * locally because the backend does not yet send a jobId for this action).
 * All subsequent frames for that run carry the same UUID so the multi-job
 * Redux slice (autoFeedActuals.jobs[jobId]) can track them independently.
 * Running a second cascade while the first is in progress therefore creates
 * a second card in the ProgressNotificationStack.
 */
export function useAutoFeedActualsSubscription() {
  const dispatch = useDispatch();
  // Tracks the UUID of the currently-running cascade so mid-job frames
  // can be routed to the right jobs[jobId] entry.
  const currentJobIdRef = useRef(null);

  useEffect(() => {
    const username = localStorage.getItem("username");
    const token = localStorage.getItem("jwtToken");
    if (!username || !token) return undefined;

    const topic = `/topic/auto-feed-actuals/${username}`;

    const unsubscribe = subscribeStomp(topic, (message) => {
      let frame;
      try {
        frame = JSON.parse(message.body);
      } catch {
        return;
      }

      const action = autoFeedActualsFrameToAction(frame);
      if (!action) return;

      // New cascade starting — generate a fresh UUID.
      if (action.type === AUTO_FEED_ACTUALS_STARTED) {
        currentJobIdRef.current = uuidv4();
      }

      const jobId = currentJobIdRef.current;
      if (!jobId) return; // ignore frames before any STARTED

      // Inject jobId into every payload so the multi-job reducer can route
      // the action to the correct jobs[jobId] entry.
      const terminatedAt = Date.now();
      dispatch({
        ...action,
        payload: {
          ...(action.payload ?? {}),
          jobId,
          // Stamp terminatedAt on terminal actions so the selector can
          // transition the card to "done"/"error" state.
          ...( (action.type === "AUTO_FEED_ACTUALS_ALL_COMPLETED" ||
                action.type === "AUTO_FEED_ACTUALS_ALL_FAILED")
              ? { terminatedAt }
              : {} ),
        },
      });
    });

    return unsubscribe;
  }, [dispatch]);
}
